import { useCallback, useMemo, useRef, useState } from "react";
import {
  ConnectorType,
  ExternalItemType,
  useBrowseExternalSourceQuery,
  useSearchExternalSourceLazyQuery,
} from "@axio/graphql/generated/graphql";
import type { ExternalSourceItem } from "@axio/graphql/generated/graphql";
import { BreadcrumbEntry } from "../../components/common/general/ContentPicker/ContentPicker.types";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ExternalBrowserItem {
  id: string;
  name: string;
  type: ExternalItemType;
  mimeType?: string | null;
  sizeBytes?: number | null;
  updatedAt: string;
  url: string;
  thumbnailUrl?: string | null;
  providerMetadata?: ExternalSourceItem["providerMetadata"];
  /** True when this item is a container that can be drilled into. */
  isFolder: boolean;
}

export interface UseExternalBrowserResult {
  items: ExternalBrowserItem[];
  loading: boolean;
  error: string | null;
  breadcrumbs: BreadcrumbEntry[];
  hasNextPage: boolean;
  searchQuery: string;
  navigateInto: (item: ExternalBrowserItem) => void;
  navigateToBreadcrumb: (index: number) => void;
  loadMore: () => void;
  setSearch: (q: string) => void;
  clearSearch: () => void;
  refetch: () => Promise<any>;
}

function toExternalBrowserItem(raw: ExternalSourceItem): ExternalBrowserItem {
  return {
    id: raw.id,
    name: raw.name,
    type: raw.type,
    mimeType: raw.mimeType,
    sizeBytes: raw.sizeBytes,
    updatedAt: raw.updatedAt,
    url: raw.url,
    thumbnailUrl: raw.thumbnailUrl,
    providerMetadata: raw.providerMetadata,
    isFolder: raw.type === ExternalItemType.Folder,
  };
}

/**
 * Manages browsing and searching a single external connector source.
 *
 * Browsing:  uses `browseExternalSource` with cursor-based pagination and a
 *            navigation stack (breadcrumbs).
 *
 * Searching: switches to `searchExternalSource` (lazy) when the user types,
 *            Clearing search returns to browse mode.
 */
export function useExternalBrowser(
  connectorType: ConnectorType | null,
  allowedItemTypes?: ExternalItemType[]
): UseExternalBrowserResult {
  // ---------- navigation stack ----------
  const [navStack, setNavStack] = useState<BreadcrumbEntry[]>([{ containerId: null, label: "Root" }]);
  const currentEntry = navStack[navStack.length - 1];

  // ---------- pagination ----------
  const [accumulatedItems, setAccumulatedItems] = useState<ExternalBrowserItem[]>([]);

  // ---------- search ----------
  const [searchQuery, setSearchQueryState] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------- Browse query ----------
  const {
    data: browseData,
    loading: browseLoading,
    error: browseError,
    fetchMore,
    refetch: refetchBrowse,
  } = useBrowseExternalSourceQuery({
    variables: {
      connectorType: connectorType ?? ConnectorType.GoogleDrive,
      input: {
        containerId: currentEntry.containerId ?? undefined,
        pageSize: PAGE_SIZE,
        cursor: undefined,
      },
    },
    skip: !connectorType || !!debouncedSearch,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const items = (data.browseExternalSource?.data?.items ?? []).map(toExternalBrowserItem);
      setAccumulatedItems(items);
    },
  });

  // ---------- Search query ----------
  const [runSearch, { data: searchData, loading: searchLoading, error: searchError }] =
    useSearchExternalSourceLazyQuery({ fetchPolicy: "network-only" });

  // ---------- Derived state ----------
  const isSearchMode = !!debouncedSearch;
  const rawItems = isSearchMode
    ? (searchData?.searchExternalSource?.data?.items ?? []).map(toExternalBrowserItem)
    : accumulatedItems;

  const browsePageInfo = browseData?.browseExternalSource?.data?.pageInfo;
  const searchPageInfo = searchData?.searchExternalSource?.data?.pageInfo;
  const pageInfo = isSearchMode ? searchPageInfo : browsePageInfo;

  // ---------- Public callbacks ----------

  const navigateInto = useCallback((item: ExternalBrowserItem) => {
    if (!item.isFolder) return;
    setNavStack((prev) => [...prev, { containerId: item.id, label: item.name }]);
    setAccumulatedItems([]);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    setNavStack((prev) => prev.slice(0, index + 1));
    setAccumulatedItems([]);
  }, []);

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage || !pageInfo.nextCursor) return;

    if (isSearchMode && connectorType) {
      runSearch({
        variables: {
          connectorType,
          input: { query: debouncedSearch, cursor: pageInfo.nextCursor, pageSize: PAGE_SIZE },
        },
      });
      return;
    }

    if (fetchMore && connectorType) {
      const nextCursor = browsePageInfo?.nextCursor;
      if (!nextCursor) return;
      void fetchMore({
        variables: {
          connectorType,
          input: {
            containerId: currentEntry.containerId ?? undefined,
            pageSize: PAGE_SIZE,
            cursor: nextCursor,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const existing = prev.browseExternalSource?.data?.items ?? [];
          const next = fetchMoreResult.browseExternalSource?.data?.items ?? [];
          return {
            ...fetchMoreResult,
            browseExternalSource: {
              ...fetchMoreResult.browseExternalSource,
              data: {
                ...fetchMoreResult.browseExternalSource?.data,
                items: [...existing, ...next],
              },
            },
          };
        },
      });
    }
  }, [pageInfo, isSearchMode, connectorType, debouncedSearch, fetchMore, browsePageInfo, currentEntry, runSearch]);

  const setSearch = useCallback(
    (q: string) => {
      setSearchQueryState(q);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setDebouncedSearch(q);
        if (q && connectorType) {
          runSearch({
            variables: {
              connectorType,
              input: { query: q, pageSize: PAGE_SIZE },
            },
          });
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    [connectorType, runSearch]
  );

  const clearSearch = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSearchQueryState("");
    setDebouncedSearch("");
  }, []);

  const error = useMemo(() => {
    if (browseError) return browseError.message;
    if (searchError) return searchError.message;
    return null;
  }, [browseError, searchError]);

  return {
    items:
      allowedItemTypes && allowedItemTypes.length > 0
        ? rawItems.filter((i) => i.type === ExternalItemType.Folder || allowedItemTypes.includes(i.type))
        : rawItems,
    loading: browseLoading || searchLoading,
    error,
    breadcrumbs: navStack,
    hasNextPage: !!pageInfo?.hasNextPage,
    searchQuery,
    navigateInto,
    navigateToBreadcrumb,
    loadMore,
    setSearch,
    clearSearch,
    refetch: refetchBrowse,
  };
}
